/**
 * RoleProtectedRoute - Route protection based on user roles
 * PermissionRoute - Route protection based on resource/action permissions
 */

import { Navigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { usePermission } from '../hooks/usePermission'
import ProtectedRoute from './ProtectedRoute'

function RoleProtectedRoute({ children, allowedRoles = [] }) {
  const { user } = useAuthStore()

  if (allowedRoles.length === 0) {
    return <ProtectedRoute>{children}</ProtectedRoute>
  }

  const userRole = user?.role || ''
  const hasAccess = allowedRoles.includes(userRole) || user?.is_admin

  if (!hasAccess) {
    return <AccessDenied />
  }

  return <ProtectedRoute>{children}</ProtectedRoute>
}

/**
 * PermissionRoute - Beschermt een route op basis van resource + action permissies.
 * Gebruik: <PermissionRoute resource="financieel" action="read"><FinancePage /></PermissionRoute>
 */
export function PermissionRoute({ children, resource, action = 'read' }) {
  const hasPermission = usePermission(resource, action)

  if (!hasPermission) {
    return (
      <ProtectedRoute>
        <AccessDenied />
      </ProtectedRoute>
    )
  }

  return <ProtectedRoute>{children}</ProtectedRoute>
}

function AccessDenied() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md">
        <div className="text-5xl mb-4">🔒</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Geen Toegang</h1>
        <p className="text-gray-600 mb-4">
          Je hebt geen rechten om deze pagina te bekijken. Neem contact op met je beheerder als je denkt dat dit een fout is.
        </p>
        <a href="/dashboard" className="text-blue-600 hover:underline text-sm">
          Terug naar Dashboard
        </a>
      </div>
    </div>
  )
}

export default RoleProtectedRoute
